import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SignalField from "./SignalField";
import { Magnetic, LiveDot } from "./ui/Primitives";

const rotatingWords = [
  "שמייצרים לידים",
  "שמגדילים מכירות",
  "שבונים מותג",
  "עם אוטומציה חכמה",
  "עם Big Data",
  "שמשנים את המשחק",
];

const stack = [
  "React",
  "Next.js",
  "TypeScript",
  "Node.js",
  "PostgreSQL",
  "Tailwind",
  "AI Agents",
  "RAG",
  "Google Ads",
  "Meta Ads",
  "GA4",
  "Big Data",
];

const SWAP_INTERVAL = 2600;
const SWAP_DURATION = 760;

const Hero: React.FC = () => {
  const [loaded, setLoaded] = useState(false);
  const [current, setCurrent] = useState(0);
  const [outgoing, setOutgoing] = useState<number | null>(null);

  // A timeout, not rAF: rAF is paused in background tabs, which would leave
  // the hero at opacity 0 for anyone who opens the site in a new tab.
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 40);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let clear: ReturnType<typeof setTimeout>;
    const interval = setInterval(() => {
      // Transitions do not advance while the tab is hidden, so a swap started
      // there would strand the incoming word off-screen until the next focus.
      if (document.hidden) return;
      setCurrent((prev) => {
        const next = (prev + 1) % rotatingWords.length;
        setOutgoing(prev);
        clear = setTimeout(() => setOutgoing(null), SWAP_DURATION);
        return next;
      });
    }, SWAP_INTERVAL);

    // Coming back to the tab, drop any half-finished swap immediately.
    const onVisible = () => {
      if (!document.hidden) setOutgoing(null);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      clearTimeout(clear);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const step = (i: number) => ({
    opacity: loaded ? 1 : 0,
    transform: loaded ? "none" : "translateY(22px)",
    transition: `opacity 900ms cubic-bezier(0.16,1,0.3,1) ${i * 90 + 120}ms, transform 900ms cubic-bezier(0.16,1,0.3,1) ${i * 90 + 120}ms`,
  });

  return (
    <section className="relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-ink">
      <SignalField />

      {/* One soft field of light behind the headline, offset and blurred so it
          reads as depth rather than as a sticker. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 right-[-10%] h-[38rem] w-[38rem] rounded-full opacity-70 blur-[120px]"
        style={{
          background:
            "radial-gradient(circle, rgba(0,229,160,0.10) 0%, rgba(77,216,232,0.05) 45%, transparent 72%)",
        }}
      />

      <div className="shell relative z-10 flex flex-1 flex-col justify-center pb-24 pt-32 sm:pb-16 lg:pt-40">
        {/* Availability */}
        <div className="mb-10 flex items-center gap-3" style={step(0)}>
          <LiveDot />
          <span className="t-meta">זמין לפרויקטים חדשים</span>
        </div>

        {/* Headline */}
        <h1 className="t-display text-fg" style={step(1)}>
          <span className="block whitespace-nowrap">פתרונות דיגיטליים</span>
          {/* Every word is mounted once and moved by transform. Nothing enters
              or leaves the DOM, so the resting state is always "current word
              at rest" : it cannot get stranded mid-animation.
              The window is taller than the cap height so Hebrew descenders
              (ק, ן, ך) are not sheared off by the mask. */}
          <span
            className="relative block text-signal"
            style={{ height: "1.22em", overflow: "hidden", marginTop: "-0.06em" }}
          >
            {rotatingWords.map((word, i) => (
              <span
                key={word}
                aria-hidden={i !== current}
                className="absolute inset-0 whitespace-nowrap leading-[1.12]"
                style={{
                  transform: `translateY(${
                    i === current ? "0%" : i === outgoing ? "-108%" : "108%"
                  })`,
                  transition:
                    i === current || i === outgoing
                      ? `transform ${SWAP_DURATION}ms cubic-bezier(0.16,1,0.3,1)`
                      : "none",
                }}
              >
                {word}
              </span>
            ))}
          </span>
        </h1>

        {/* Lead */}
        <p className="t-lead mt-10 max-w-[46ch]" style={step(2)}>
          מעל 25 שנות ניסיון בפיתוח אתרים ואפליקציות, סוכנים חכמים ואוטומציה,
          ניתוח Big Data, ויזואליזציית דוחות, קמפיינים ממומנים וחדשנות יצירתית
        </p>

        {/* Actions */}
        <div className="mt-12 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" style={step(3)}>
          <Magnetic>
            <Link to="/contact" className="btn btn-primary group w-full sm:w-auto">
              <span>בואו נדבר על הפרויקט שלכם</span>
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 ease-out-expo group-hover:-translate-x-1" />
            </Link>
          </Magnetic>
          <Link to="/portfolio" className="btn btn-ghost w-full sm:w-auto">
            צפו בעבודות שלנו
          </Link>
        </div>

        {/* Measured facts, set in the data face */}
        <div className="mt-16 border-t border-[var(--line)] pt-6" style={step(4)}>
          <dl className="flex flex-wrap gap-x-12 gap-y-5">
            {[
              { v: "25+", k: "שנות ניסיון" },
              { v: "200+", k: "פרויקטים" },
              { v: "24/7", k: "זמינות" },
            ].map((item) => (
              <div key={item.k} className="flex items-baseline gap-2.5">
                {/* dir=ltr keeps "25+" from rendering as "+25" inside the RTL flow */}
                <dt className="t-data text-2xl text-fg" dir="ltr">
                  {item.v}
                </dt>
                <dd className="text-sm text-fg-3">{item.k}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Stack marquee closes the hero */}
      <div
        className="relative z-10 overflow-hidden border-t border-[var(--line)] bg-ink/70 py-3.5 backdrop-blur-sm"
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 1.2s ease 900ms" }}
        aria-hidden="true"
      >
        <div className="flex w-max animate-marquee gap-10 pe-10 motion-reduce:animate-none">
          {[...stack, ...stack].map((item, i) => (
            <span key={i} className="t-data whitespace-nowrap text-xs text-fg-3">
              {item}
            </span>
          ))}
        </div>
      </div>

    </section>
  );
};

export default Hero;
